import { useMemo } from "react";
import { patchLayout } from "../api/chains";
import { useChainStore } from "../store/chainStore";
import { useLayoutStore, type XY } from "../store/layoutStore";

const FLUSH_DELAY_MS = 400;

interface DirtyEntry {
  chainId: string;
  pos: XY;
}

const dirtyPositions = new Map<string, DirtyEntry>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function markEntries(chainId: string, positions: Record<string, XY>) {
  const layout = useLayoutStore.getState();
  for (const [id, pos] of Object.entries(positions)) {
    dirtyPositions.set(id, { chainId, pos });
    layout.markPending(id);
  }
}

function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (dirtyPositions.size === 0) return;

  const groups = new Map<
    string,
    { ids: string[]; snapshot: Record<string, XY> }
  >();
  for (const [id, entry] of dirtyPositions) {
    let group = groups.get(entry.chainId);
    if (!group) {
      group = { ids: [], snapshot: {} };
      groups.set(entry.chainId, group);
    }
    group.ids.push(id);
    group.snapshot[id] = entry.pos;
  }
  dirtyPositions.clear();

  for (const [chainId, { ids, snapshot }] of groups) {
    patchLayout(chainId, snapshot)
      .then(() => {
        const layout = useLayoutStore.getState();
        for (const id of ids) layout.clearPending(id);
      })
      .catch((err) => {
        console.error("Failed to persist layout", err);
        for (const [id, pos] of Object.entries(snapshot)) {
          if (!dirtyPositions.has(id)) {
            dirtyPositions.set(id, { chainId, pos });
          }
        }
      });
  }
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

export interface LayoutSaver {
  enqueue: (positions: Record<string, XY>) => void;
  flushNow: (positions: Record<string, XY>) => void;
}

export function useLayoutSaver(): LayoutSaver {
  return useMemo<LayoutSaver>(
    () => ({
      enqueue: (positions) => {
        if (Object.keys(positions).length === 0) return;
        const chainId = useChainStore.getState().chainId;
        if (chainId == null) return;
        markEntries(chainId, positions);
        scheduleFlush();
      },
      flushNow: (positions) => {
        const chainId = useChainStore.getState().chainId;
        if (chainId == null) return;
        if (Object.keys(positions).length > 0) markEntries(chainId, positions);
        flush();
      },
    }),
    [],
  );
}
